import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export interface OnboardingFooterProps {
  viewStep: number;
  totalSteps: number;
  isLastStep: boolean;
  isAccountantFlowOwner: boolean;
  isAccountantUser?: boolean;
  situationChoice?: "scratch" | "issuer_need_regularization" | "already_issuer" | null;
  dgiPasswordReady?: boolean;
  dgiStepsCompletedConfirmed?: boolean;
  canFinish: boolean;
  finishing: boolean;
  nextDisabled: boolean;
  nextLoading: boolean;
  onBack: () => void;
  onNext: () => void;
  onFinish: () => void;
}

export function OnboardingFooter({
  viewStep,
  totalSteps,
  isLastStep,
  isAccountantFlowOwner,
  isAccountantUser = false,
  canFinish,
  finishing,
  nextDisabled,
  nextLoading,
  situationChoice,
  dgiPasswordReady = false,
  dgiStepsCompletedConfirmed = false,
  onBack,
  onNext,
  onFinish,
}: OnboardingFooterProps): JSX.Element | null {
  const { t } = useTranslation();
  const isDgiStep = viewStep === 3;

  if (viewStep === 0 || viewStep === 1 || isDgiStep) {
    return null;
  }

  if ((viewStep === 8 && isAccountantUser) || (viewStep === 8 && isAccountantFlowOwner)) {
    return null;
  }

  const handlePrimaryClick = (): void => {
    if (isDgiStep) onNext();
    else if (isLastStep) onFinish();
    else onNext();
  };

  const primaryDisabled = isDgiStep
    ? !dgiPasswordReady || !dgiStepsCompletedConfirmed
    : isLastStep
      ? !canFinish || finishing
      : nextDisabled;
  const primaryLoading = isLastStep && !isDgiStep ? finishing : nextLoading;
  const showNext = true;
  const showBack =
    isDgiStep || (!isLastStep && viewStep >= 2);

  return (
    <Card>
      <CardContent className="flex flex-row items-center justify-between pt-6">
        <div>
          {showBack ? (
            <Button type="button" variant="outline" onClick={onBack}>
              {t("onboarding.back")}
            </Button>
          ) : (
            <span />
          )}
        </div>
        <div className="flex gap-2">
          {showNext && (
          <Button
            onClick={handlePrimaryClick}
            disabled={primaryDisabled}
            className="ml-auto transition-colors duration-150"
          >
            {primaryLoading && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {isDgiStep
              ? t("onboarding.next")
              : isLastStep
                ? isAccountantUser
                  ? t("onboarding.next")
                  : isAccountantFlowOwner
                    ? finishing
                      ? t("onboarding.finishing")
                      : t("onboarding.completeSetup")
                    : finishing
                      ? t("onboarding.finishing")
                      : t("onboarding.goToDashboard")
                : t("onboarding.next")}
          </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
